/**
 * `<fp-document-workspace>` — framework-agnostic wrapper around the React
 * document viewer.
 *
 * Renders into an OPEN shadow root so a host application's global CSS cannot
 * reach the viewer and the viewer's CSS cannot leak out. Events are dispatched
 * with `composed: true` so they cross the shadow boundary into the host.
 *
 * Interaction (clicks, text selection) is captured here, on the shadow root,
 * rather than through DocumentViewer's callbacks. That keeps this wrapper
 * additive -- the viewer is not modified -- and it covers more ground: the
 * viewer's own onBlockClick only fires on a direct hit against a keyed block,
 * whereas the gutters, padding and inter-block margins account for a large part
 * of the visible document. Clicks there resolve to the nearest block instead of
 * being dropped.
 */
import { createElement } from 'react';

import { createRoot, type Root } from 'react-dom/client';

import {
  ATTRIBUTES,
  BETA_EXPERIENCE_EXCLUDED_PATH_SEGMENTS,
  BLOCK_DATA_ATTRIBUTES,
  CLICK_TARGET_SELECTOR,
  ERROR_CODES,
  EVENTS,
  HOTJAR_BETA_QUERY_PARAM,
  HOTJAR_BETA_QUERY_VALUE,
  OBSERVED_ATTRIBUTES,
  VIEWER_CONTAINER_SELECTOR,
} from '../constants/element';
import { blockKey } from '../renderer/contractRenderer';
import { formatZodError, ManuscriptContractShapeSchema } from '../schemas/contract';
import { adoptStyles, releaseStyles } from '../styles/adoptStyles';

import { createBaseUrlResolver } from './assetBaseUrl';
import { DocumentWorkspaceRoot } from './DocumentWorkspaceRoot';

import type { BlockRole, ContractBlock, ManuscriptContract } from '../types/contract';
import type {
  DocumentWorkspaceConfig,
  ResolveHref,
  TextClickedDetail,
  TextSelectedDetail,
} from '../types/element';
import type { DocumentErrorEvent } from '../types/events';

/** Properties that must survive being set before the element upgrades. */
const UPGRADABLE_PROPERTIES = ['contract', 'highlightedBlockId', 'resolveHref'] as const;

export class DocumentWorkspaceElement extends HTMLElement {
  static get observedAttributes(): readonly string[] {
    return OBSERVED_ATTRIBUTES;
  }

  private static _historyPatched = false;

  /**
   * Latches true the first time any `<fp-document-workspace>` instance
   * connects, and never resets. Only some journals get the beta UI -- the
   * rest still render the old plain manuscript form on the exact same
   * `/submission/flow?step=1` URL shape, so path/step alone cannot tell
   * "beta journal" apart from "old journal". Whether this element ever
   * actually mounts is the only real signal: Hotjar must never target a
   * journal where it never appears at all. Once it has appeared, though, it
   * keeps mattering even on pages that never mount it themselves (Services,
   * Preview) -- see `_syncBetaExperienceUrlParam`.
   */
  private static _everConnected = false;

  /**
   * Wraps `history.pushState`/`replaceState` once for the whole page, so the
   * beta URL param stays correct (present everywhere in the flow, absent only
   * on File Upload and the journal-selection Create page) across DSW's own
   * navigation.
   *
   * Deliberately NOT tied to any element instance being *currently*
   * connected: DSW does not render `<fp-document-workspace>` on every page of
   * the flow (Services and Preview have no manuscript to show), so a version
   * of this that only reacted while an instance was mounted went stale on
   * exactly those pages -- `fp` would vanish on Services and stay gone until
   * the element remounted. `_syncBetaExperienceUrlParam` reads only
   * `window.location`/`window.history`, never `this`, so it can run globally
   * instead. (It is still gated on having *ever* connected at least once --
   * see `_everConnected` -- just not on being connected *right now*.) Called
   * once from `defineDocumentWorkspace()`, which DSW's bootstrap runs once
   * per page load, this keeps reacting for the rest of that page's lifetime
   * regardless of which steps mount or unmount the element itself.
   *
   * DSW's `FlowComponent.redirectUrl()` rebuilds the whole URL via
   * `router.navigateByUrl()` on every Next/Previous/stepper click -- which
   * happens to be exactly the moment the path itself changes (File Upload's
   * `/submission/fileUpload` to the rest of the flow's `/submission/flow`,
   * or back), so reacting to any URL change and re-deriving from the new path
   * is sufficient. Patching the History API is the only way to react to
   * that: it fires regardless of *why* the URL changed, and covers
   * `popstate` (the actual browser back/forward buttons, as opposed to DSW's
   * own Previous button) too. `_syncBetaExperienceUrlParam`'s own no-op check
   * (comparing against the *desired* state, not just presence) keeps this
   * from looping when its own `replaceState` call re-triggers this handler.
   *
   * No eager sync on install: at the point this runs (DSW's script tag,
   * loaded before Angular even bootstraps), no element could possibly have
   * connected yet, so it would always be a no-op anyway -- the first real
   * sync happens from `connectedCallback` once an instance actually appears.
   */
  static _ensureHistoryPatched(): void {
    if (DocumentWorkspaceElement._historyPatched) return;
    if (typeof window === 'undefined' || typeof window.history === 'undefined') return;
    DocumentWorkspaceElement._historyPatched = true;

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);
    const reassert = () => DocumentWorkspaceElement._syncBetaExperienceUrlParam();

    window.history.pushState = function (...args: Parameters<History['pushState']>) {
      originalPushState(...args);
      reassert();
    };
    window.history.replaceState = function (
      ...args: Parameters<History['replaceState']>
    ) {
      originalReplaceState(...args);
      reassert();
    };
    window.addEventListener('popstate', reassert);
  }

  private _contract: ManuscriptContract | null = null;
  /**
   * `undefined` = never assigned via the property, defer to the attribute.
   * `null` = explicitly cleared via the property; must win over the attribute,
   * or `highlightBlock(null)` could never actually clear a stale attribute.
   */
  private _highlightedBlockId: string | null | undefined = undefined;
  private _resolveHref: ResolveHref | null = null;

  private _root: Root | null = null;
  private _shadow: ShadowRoot | null = null;
  private _fetchController: AbortController | null = null;
  private _connected = false;

  /**
   * Cached viewport-relative positions for `_nearestBlock`'s fallback search.
   * `null` means "needs remeasuring" -- cleared whenever the document's content
   * changes, its size changes, or the shadow root's content scrolls (the cached
   * `top`/`bottom` are viewport-relative, so scrolling invalidates them even
   * though nothing in the DOM actually changed).
   */
  private _blockPositions: BlockPosition[] | null = null;
  private _blockMutationObserver: MutationObserver | null = null;
  private _blockResizeObserver: ResizeObserver | null = null;
  private _observersAttached = false;
  private _invalidateBlocks: (() => void) | null = null;

  // ---------------------------------------------------------------- properties

  get contract(): ManuscriptContract | null {
    return this._contract;
  }

  set contract(value: ManuscriptContract | null) {
    if (value !== null) {
      const shapeError = this._validateContractShape(value);

      if (shapeError) {
        this._emitError(ERROR_CODES.CONTRACT_INVALID, `Invalid contract: ${shapeError}`);
        return;
      }
    }

    this._contract = value;
    // An explicit contract wins over `contract-url`; drop any in-flight fetch.
    this._abortFetch();
    this._render();
  }

  get highlightedBlockId(): string | null {
    return this._highlightedBlockId === undefined
      ? this.getAttribute(ATTRIBUTES.HIGHLIGHTED_BLOCK_ID)
      : this._highlightedBlockId;
  }

  set highlightedBlockId(value: string | null) {
    this._highlightedBlockId = value;
    this._render();
  }

  get resolveHref(): ResolveHref | null {
    return this._resolveHref;
  }

  set resolveHref(value: ResolveHref | null) {
    this._resolveHref = value;
    this._render();
  }

  // ------------------------------------------------------------------- methods

  /** Imperative API for hosts holding an element reference. */
  highlightBlock(blockId: string | null): void {
    this.highlightedBlockId = blockId;
  }

  /**
   * Inserts a block, or replaces the existing block carrying the same id.
   *
   * This is the inbound direction: a host pushes content into the rendered
   * document. Assigning a whole new `contract` achieves the same thing, but that
   * makes the host own cloning the block array; this keeps the common case to
   * one call. Either way the change flows through the normal render path, so the
   * viewer re-renders and `onDocumentReady` re-fires with the new block count.
   *
   * Identity follows the renderer's `blockKey`: an explicit `id` wins, else the
   * `role` (unique per manuscript by contract rule). A block with neither is
   * always appended -- matching on two absent ids would silently replace the
   * first unkeyed block, and the extraction service emits no ids in v1.
   *
   * @param block  Block to insert or replace.
   * @param index  Optional position for a NEW block; appended when omitted.
   *               Ignored when replacing, which keeps its original position.
   */
  upsertBlock(block: ContractBlock, index?: number): void {
    const current = this._contract;

    if (!current) {
      this._emitError(
        ERROR_CODES.NO_CONTRACT,
        'upsertBlock() needs a contract; set `contract` or `contract-url` first'
      );
      return;
    }

    // Match on the field the incoming block actually carries. Collapsing both
    // into one "identity" value is wrong: a block that has an id would then
    // never be found by a role lookup, even when the roles agree.
    const existing =
      block.id != null
        ? current.blocks.findIndex((candidate) => candidate.id === block.id)
        : block.role != null
          ? current.blocks.findIndex((candidate) => candidate.role === block.role)
          : -1;

    let blocks: ContractBlock[];
    if (existing >= 0) {
      blocks = current.blocks.map((candidate, at) =>
        at === existing ? block : candidate
      );
    } else {
      blocks = [...current.blocks];
      blocks.splice(index ?? blocks.length, 0, block);
    }

    // Through the accessor, so the render path and fetch cancellation are shared.
    this.contract = { ...current, blocks };
  }

  /**
   * Removes the block with the given key. No-op when it is not present.
   *
   * The key is the same value `onTextClicked` reports and `highlightBlock()`
   * accepts, so a host can round-trip it without knowing the contract's shape.
   */
  removeBlock(key: string): void {
    const current = this._contract;

    if (!current) return;

    const blocks = current.blocks.filter(
      (candidate, at) => blockKey(candidate, at) !== key
    );

    if (blocks.length !== current.blocks.length) {
      this.contract = { ...current, blocks };
    }
  }

  // ----------------------------------------------------------------- lifecycle

  connectedCallback(): void {
    // First connect ever (for any instance, this page session): latch the
    // beta-URL sync on and run it immediately, since only connecting here
    // proves this is one of the journals with the beta UI -- see
    // `_everConnected`. Every later connect is a no-op re-set of the same
    // flag; the History patch (installed once via defineDocumentWorkspace)
    // keeps the sync current after that regardless of this element's own
    // connect/disconnect state.
    DocumentWorkspaceElement._everConnected = true;
    DocumentWorkspaceElement._syncBetaExperienceUrlParam();

    if (!this._shadow) {
      this._shadow = this.attachShadow({ mode: 'open' });
    }

    this._attachObservers(this._shadow);

    // Every connect, not just the first: disconnectedCallback releases the
    // previous adoption (see releaseStyles), so a reconnect needs a fresh one.
    adoptStyles(this._shadow);

    // A host may assign properties before the class definition loads; those land
    // as own properties that shadow the prototype accessors. Re-apply them.
    for (const name of UPGRADABLE_PROPERTIES) {
      this._upgradeProperty(name);
    }

    this._connected = true;
    this._render();

    if (!this._contract) {
      void this._loadContractFromUrl();
    }
  }

  disconnectedCallback(): void {
    this._connected = false;
    this._abortFetch();
    this._detachObservers();

    if (this._shadow) releaseStyles(this._shadow);

    // Deferred, and _root is deliberately left in place until the microtask
    // actually runs (not nulled here). A host moving this element in the DOM
    // (remove() then re-append, or just re-inserting it) fires disconnect then
    // reconnect back-to-back in the same synchronous tick, before any queued
    // microtask gets a chance to run. Nulling _root immediately would make
    // _render()'s `_root ??= createRoot(...)` create a SECOND root on the same
    // shadow DOM node while this one is still pending teardown -- two React
    // roots briefly attached to one container, which is exactly the kind of
    // thing React warns about and behaves unpredictably around. Checking
    // `_connected` again when the microtask runs is what avoids that: if the
    // element reconnected in the meantime, this root is still the one in use,
    // so leave it alone instead of tearing it down.
    queueMicrotask(() => {
      if (this._connected) return;
      this._root?.unmount();
      this._root = null;
    });
  }

  attributeChangedCallback(
    name: string,
    previous: string | null,
    next: string | null
  ): void {
    if (previous === next) return;

    if (name === ATTRIBUTES.CONTRACT_URL) {
      if (this._connected && !this._contract) void this._loadContractFromUrl();
      return;
    }

    this._render();
  }

  /**
   * Attaches the click/selection listeners and the position-cache-invalidating
   * observers to `shadow`. Idempotent (guarded by `_observersAttached`) so a
   * reconnect after `disconnectedCallback` re-attaches cleanly instead of
   * stacking duplicate listeners.
   */
  private _attachObservers(shadow: ShadowRoot): void {
    if (this._observersAttached) return;
    this._observersAttached = true;

    shadow.addEventListener('click', this._handleClick);
    shadow.addEventListener('mouseup', this._handleSelection);

    // Invalidate the block-position cache on anything that could move a
    // block: content changing, the element resizing, or its content
    // scrolling (getBoundingClientRect() is viewport-relative, so a scroll
    // invalidates the cache even though the DOM itself did not change).
    const invalidate = () => {
      this._blockPositions = null;
    };

    this._blockMutationObserver = new MutationObserver(invalidate);
    this._blockMutationObserver.observe(shadow, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    // Feature-detect: ResizeObserver is universal in real browsers but does
    // not exist in jsdom, and an uncaught throw here would abort the rest of
    // connectedCallback (the render and initial contract-url fetch below).
    if (typeof ResizeObserver !== 'undefined') {
      this._blockResizeObserver = new ResizeObserver(invalidate);
      this._blockResizeObserver.observe(this);
    }
    shadow.addEventListener('scroll', invalidate, { capture: true });
    this._invalidateBlocks = invalidate;
  }

  /** Undoes `_attachObservers`, freeing the observers when the element leaves the DOM for good. */
  private _detachObservers(): void {
    if (!this._observersAttached || !this._shadow) return;
    this._observersAttached = false;

    this._shadow.removeEventListener('click', this._handleClick);
    this._shadow.removeEventListener('mouseup', this._handleSelection);
    if (this._invalidateBlocks) {
      this._shadow.removeEventListener('scroll', this._invalidateBlocks, {
        capture: true,
      });
    }
    this._blockMutationObserver?.disconnect();
    this._blockMutationObserver = null;
    this._blockResizeObserver?.disconnect();
    this._blockResizeObserver = null;
  }

  // -------------------------------------------------------------- interaction

  /**
   * Resolves a click to the nearest block-like element and reports its text.
   * `composedPath()[0]` is used because `event.target` is retargeted to the host
   * element once the event escapes the shadow boundary.
   */
  private _handleClick = (event: Event): void => {
    const origin = event.composedPath()[0];

    if (!(origin instanceof Element)) return;

    // A direct hit only covers the block's own box. The centred column's
    // gutters, the viewer's padding and the 1em margins between blocks all
    // belong to ancestors, so roughly a third of the visible document would
    // otherwise swallow clicks with no feedback. Fall back to whichever block
    // is vertically nearest the pointer.
    const target =
      origin.closest(CLICK_TARGET_SELECTOR) ?? this._nearestBlock(origin, event);

    if (!target || !this._shadow?.contains(target)) return;

    // BlockAttributes puts these on the rendered block; a click on inline markup
    // resolves upward to the nearest one.
    const keyed = target.closest(`[${BLOCK_DATA_ATTRIBUTES.KEY}]`);

    this._emit<TextClickedDetail>(EVENTS.TEXT_CLICKED, {
      blockKey: keyed?.getAttribute(BLOCK_DATA_ATTRIBUTES.KEY) ?? null,
      blockType: keyed?.getAttribute(BLOCK_DATA_ATTRIBUTES.TYPE) ?? null,
      role: (keyed?.getAttribute(BLOCK_DATA_ATTRIBUTES.ROLE) as BlockRole | null) ?? null,
      tagName: target.tagName.toLowerCase(),
      text: elementText(target),
    });
  };

  /**
   * Finds the block whose vertical extent contains the pointer, or failing that
   * the vertically closest one. Scoped to the clicked container so a click on
   * future chrome outside the document cannot resolve to a block.
   *
   * Returns `null` rather than guessing when there is no real pointer position
   * to measure from (a synthetic `click` dispatched without a `MouseEvent`, e.g.
   * `element.click()` or a test harness). Silently treating that as "pointer at
   * y=0" would resolve every such call to whichever block is closest to the top
   * of the viewport -- a wrong answer stated with the same confidence as a real
   * one, which is worse than admitting the position is unknown.
   */
  private _nearestBlock(origin: Element, event: Event): Element | null {
    if (!(event instanceof MouseEvent)) return null;

    const container = origin.closest(VIEWER_CONTAINER_SELECTOR) ?? this._shadow;

    if (!container) return null;

    const y = event.clientY;
    const positions = (this._blockPositions ??= measureBlocks(container));

    let closest: Element | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const { element, top, bottom } of positions) {
      // Zero distance when the pointer is level with the block.
      const distance = y < top ? top - y : y > bottom ? y - bottom : 0;

      if (distance < bestDistance) {
        bestDistance = distance;
        closest = element;
        if (distance === 0) break;
      }
    }

    return closest;
  }

  /** Reports a text selection made inside the shadow root. */
  private _handleSelection = (): void => {
    const text = readSelection(this._shadow);

    if (text) {
      this._emit<TextSelectedDetail>(EVENTS.TEXT_SELECTED, { text });
    }
  };

  // ------------------------------------------------------------------ internals

  private _upgradeProperty(name: (typeof UPGRADABLE_PROPERTIES)[number]): void {
    if (!Object.prototype.hasOwnProperty.call(this, name)) return;
    const self = this as unknown as Record<string, unknown>;
    const value = self[name];

    delete self[name];
    self[name] = value;
  }

  /**
   * Adds or removes the beta-experience query param on the host page's URL,
   * to match whether the URL's own path currently says File Upload or the
   * journal-selection Create page -- present everywhere else in the flow,
   * absent on either of those two only.
   *
   * `replaceState`, not `pushState`: this must not add a back-button stop, or
   * navigating back would land the user on a URL identical to the one they
   * were already on. Safe to call repeatedly -- a URL already in the right
   * state is a no-op, so this never fights with itself when the shared
   * History API patch re-triggers it.
   *
   * Static, and reads only `window.location`/`window.history`: this must
   * keep working on pages where no `<fp-document-workspace>` instance is
   * mounted at all (Services, Preview), so it cannot depend on any instance
   * state. See `_ensureHistoryPatched` for how it gets called without one.
   *
   * Gated on `_everConnected`: some journals never mount this element at
   * all (the old plain manuscript form), on the exact same URL shape as the
   * journals that do -- so until an instance has connected at least once
   * this session, there is nothing to mark, on any page.
   */
  private static _syncBetaExperienceUrlParam(): void {
    if (typeof window === 'undefined' || typeof window.history === 'undefined') return;
    if (!DocumentWorkspaceElement._everConnected) return;

    try {
      const url = new URL(window.location.href);
      const onExcludedPage = BETA_EXPERIENCE_EXCLUDED_PATH_SEGMENTS.some((segment) =>
        url.pathname.includes(segment)
      );
      const shouldBeMarked = !onExcludedPage;
      const alreadyMarked =
        url.searchParams.get(HOTJAR_BETA_QUERY_PARAM) === HOTJAR_BETA_QUERY_VALUE;

      if (shouldBeMarked === alreadyMarked) return;

      if (shouldBeMarked) {
        url.searchParams.set(HOTJAR_BETA_QUERY_PARAM, HOTJAR_BETA_QUERY_VALUE);
      } else {
        url.searchParams.delete(HOTJAR_BETA_QUERY_PARAM);
      }

      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Must never propagate: this runs inside the patched pushState/
      // replaceState, so a throw here would surface at whoever called
      // those -- e.g. Angular's Router -- and could abort a real
      // navigation. Concretely hit against jsdom's about:blank (an
      // opaque-path URL its History-impl chokes on), which is exactly
      // the document the element-bundle verification script evaluates
      // this in; a failed sync just leaves the URL as-is for the next
      // real navigation to correct.
    }
  }

  private _abortFetch(): void {
    this._fetchController?.abort();
    this._fetchController = null;
  }

  /**
   * `resolveHref` derived from `asset-base-url`, cached by the attribute value
   * it was built from.
   *
   * `_resolveConfig()` runs on every `_render()` call -- any property change,
   * attribute change, or `upsertBlock`/`removeBlock` -- so a fresh
   * `createBaseUrlResolver(...)` closure every time would hand `DocumentViewer`
   * a new function identity on every unrelated update. Its `useMemo` keys on
   * that reference, so it would recompute the whole document's rendered HTML
   * even when nothing relevant changed. Caching by the attribute's own value
   * (not just "was it called before") means a real change to `asset-base-url`
   * still produces a new resolver.
   */
  private _cachedBaseUrlResolver: { assetBaseUrl: string; resolver: ResolveHref } | null =
    null;

  private _resolveBaseUrlAttribute(assetBaseUrl: string | null): ResolveHref | null {
    if (!assetBaseUrl) return null;

    if (this._cachedBaseUrlResolver?.assetBaseUrl !== assetBaseUrl) {
      this._cachedBaseUrlResolver = {
        assetBaseUrl,
        resolver: createBaseUrlResolver(assetBaseUrl),
      };
    }

    return this._cachedBaseUrlResolver.resolver;
  }

  private _resolveConfig(): DocumentWorkspaceConfig {
    const assetBaseUrl = this.getAttribute(ATTRIBUTES.ASSET_BASE_URL);
    const resolveHref: ResolveHref | null =
      this._resolveHref ?? this._resolveBaseUrlAttribute(assetBaseUrl);

    return {
      contract: this._contract,
      highlightedBlockId: this.highlightedBlockId,
      resolveHref,
    };
  }

  private _emit = <T>(type: string, detail: T): void => {
    this.dispatchEvent(
      new CustomEvent<T>(type, { bubbles: true, composed: true, detail })
    );
  };

  private _emitError(code: string, message: string): void {
    this._emit<DocumentErrorEvent>(EVENTS.DOCUMENT_ERROR, { code, message });
  }

  /**
   * Shallow envelope check shared by every place a contract enters the
   * element (the `contract` property, and the `contract-url` fetch path).
   * Deep, per-block validation happens later, inside `renderContract` --
   * failing that only degrades the offending block, not the whole document.
   */
  private _validateContractShape(value: unknown): string | null {
    const result = ManuscriptContractShapeSchema.safeParse(value);

    return result.success ? null : formatZodError(result.error);
  }

  private async _loadContractFromUrl(): Promise<void> {
    const url = this.getAttribute(ATTRIBUTES.CONTRACT_URL);

    if (!url) return;

    this._abortFetch();
    const controller = new AbortController();

    this._fetchController = controller;

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const parsed = (await response.json()) as ManuscriptContract;

      if (controller.signal.aborted) return;

      const shapeError = this._validateContractShape(parsed);

      if (shapeError) {
        this._emitError(
          ERROR_CODES.CONTRACT_INVALID,
          `Contract at ${url} is invalid: ${shapeError}`
        );
        return;
      }

      this._contract = parsed;
      this._render();
    } catch (error) {
      if (controller.signal.aborted) return;
      const message = error instanceof Error ? error.message : String(error);

      this._emitError(
        ERROR_CODES.CONTRACT_FETCH_FAILED,
        `Failed to load ${url}: ${message}`
      );
    } finally {
      if (this._fetchController === controller) this._fetchController = null;
    }
  }

  private _render(): void {
    if (!this._connected || !this._shadow) return;

    // Re-render rather than remount, so the editor keeps its state.
    this._root ??= createRoot(this._shadow);
    this._root.render(
      createElement(DocumentWorkspaceRoot, { ...this._resolveConfig(), emit: this._emit })
    );
  }
}

interface BlockPosition {
  element: Element;
  top: number;
  bottom: number;
}

/**
 * Viewport-relative vertical extent of every click-target element in
 * `container`, for `_nearestBlock`'s fallback search.
 *
 * Reading `getBoundingClientRect()` for every match is exactly the cost
 * `_nearestBlock` used to pay on every single click; measuring once per content
 * change instead of once per click is the whole point of caching the result.
 */
function measureBlocks(container: Element | ShadowRoot): BlockPosition[] {
  const positions: BlockPosition[] = [];

  for (const element of container.querySelectorAll(CLICK_TARGET_SELECTOR)) {
    const rect = element.getBoundingClientRect();

    if (rect.height === 0) continue;
    positions.push({ element, top: rect.top, bottom: rect.bottom });
  }

  return positions;
}

/** Text of a clicked element; images have none, so fall back to alt text. */
function elementText(target: Element): string {
  if (target instanceof HTMLImageElement) {
    return target.alt || '(image)';
  }
  return (target.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Reads the current selection as collapsed-whitespace text.
 *
 * Chromium exposes selections inside a shadow root through the non-standard
 * `ShadowRoot.getSelection()`; Firefox and Safari report them on
 * `window.getSelection()`. Take the first source that yields text rather than
 * the first that yields an object: engines differ in which one returns an empty
 * Selection versus none at all.
 */
function readSelection(shadow: ShadowRoot | null): string {
  const shadowRoot = shadow as
    (ShadowRoot & { getSelection?: () => Selection | null }) | null;

  const candidates = [shadowRoot?.getSelection?.(), window.getSelection()];

  for (const selection of candidates) {
    const text = (selection?.toString() ?? '').replace(/\s+/g, ' ').trim();

    if (text) return text;
  }

  return '';
}
